import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, MessageCircle, Send,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { fetchSettings, createOrder } from '../lib/api';
import { BRAND } from '../mock';

const WHATSAPP_INTL = BRAND.whatsappIntl;

const random4 = () =>
  String(Math.floor(1000 + Math.random() * 9000));

export default function CheckoutPage() {
  const { items, subtotal, totalQty, coupon, clear } = useCart();
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    order_code_prefix: 'LEILA',
    coupon_code: 'LEILA200',
    coupon_discount: 200,
    coupon_min_items: 2,
  });

  const [form, setForm] = useState({ name: '', phone: '', address: '', note: '', hp: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Client-side order code preview (stable across renders)
  const [orderCodeSuffix] = useState(random4);
  const orderCode = `${settings.order_code_prefix || 'LEILA'}-${orderCodeSuffix}`;

  // Redirect if cart empty
  useEffect(() => {
    if (items.length === 0) navigate('/sepet', { replace: true });
  }, [items.length, navigate]);

  useEffect(() => {
    fetchSettings().then((st) => setSettings((s) => ({ ...s, ...st }))).catch(() => {});
  }, []);

  const couponValid = useMemo(
    () =>
      coupon.trim().toUpperCase() === (settings.coupon_code || 'LEILA200').toUpperCase() &&
      totalQty >= (settings.coupon_min_items || 2),
    [coupon, settings, totalQty]
  );
  const discount = couponValid ? (settings.coupon_discount || 200) : 0;
  const total = Math.max(subtotal - discount, 0);

  const formValid =
    form.name.trim().length > 1 &&
    form.phone.trim().length >= 7 &&
    form.address.trim().length > 4;
  const canSubmit = formValid && !submitting;

  const submitAll = async () => {
    if (!canSubmit) return;
    if (form.hp) return; // honeypot triggered
    setError('');
    setSubmitting(true);
    try {
      // 1) create order
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          image: i.image,
          color: i.color,
          size: i.size,
          qty: i.qty,
        })),
        customer_name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        note: form.note.trim(),
        coupon: coupon.trim(),
        code: orderCode,
        hp: form.hp,
      };
      const created = await createOrder(payload);

      // 2) build WhatsApp message and redirect
      const itemLines = created.items
        .map((i) => `- ${i.name} (${i.color}, Beden ${i.size}) x${i.qty} - ₺${i.price.toLocaleString('tr-TR')}`)
        .join('\n');
      const msg =
        `Merhaba Leila Tesettür,\n\nSiparişimi onaylıyorum.\n\n` +
        `Sipariş Kodu: ${created.code}\n` +
        `Ad Soyad: ${created.customer_name}\n` +
        `Telefon: ${created.phone}\n` +
        `Adres: ${created.address}\n\n` +
        `Ürünler:\n${itemLines}\n\n` +
        (created.note ? `Not: ${created.note}\n\n` : '') +
        (created.discount ? `İndirim: -₺${created.discount.toLocaleString('tr-TR')}\n` : '') +
        `Toplam: ₺${created.total.toLocaleString('tr-TR')}`;
      const wa = `https://wa.me/${WHATSAPP_INTL}?text=${encodeURIComponent(msg)}`;
      clear();
      window.location.href = wa;
    } catch (err) {
      setError(err?.response?.data?.detail || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      setSubmitting(false);
    }
  };

  const submitLabel = !formValid
    ? 'Bilgileri Doldurun'
    : submitting
      ? 'Yönlendiriliyor...'
      : 'Siparişi Tamamla';

  return (
    <div className="min-h-screen bg-[#f7f2ec] pb-24">
      {/* Top nav row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-8">
        <div className="flex items-center justify-between py-6 border-b border-stone-200">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/leila-logo.jpg"
              alt="Leila Tesettür"
              className="h-14 w-14 rounded-full object-cover ring-1 ring-stone-200"
            />
            <span className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Ödeme</span>
          </Link>
          <Link
            to="/sepet"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-stone-700 hover:text-stone-950 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Sepete Dön
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10 grid lg:grid-cols-3 gap-10">
        {/* LEFT column */}
        <div className="lg:col-span-2 space-y-8">
          {/* header */}
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-stone-500 inline-flex items-center gap-2">
              <MessageCircle className="h-4 w-4" /> WhatsApp ile Sipariş
            </p>
            <h1 className="mt-3 font-serif text-4xl lg:text-6xl text-stone-900">
              Siparişinizi <em className="italic font-light">tamamlayın</em>
            </h1>
          </div>

          <div className="bg-white/60 border border-stone-200 p-4" data-testid="checkout-order-code">
            <p className="text-[14.5px] text-stone-800">
              <span className="text-stone-500">Sipariş Kodu: </span>
              <span className="font-mono font-semibold text-stone-900">{orderCode}</span>
            </p>
          </div>

          {/* Delivery form */}
          <div className="bg-white/60 border border-stone-200 p-6 lg:p-8 space-y-5">
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp}
              onChange={(e) => setForm({ ...form, hp: e.target.value })}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />
            <p className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Teslimat Bilgileriniz</p>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Ad Soyad *</label>
              <input
                required
                data-testid="checkout-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Adınız ve soyadınız"
                className="mt-2 w-full bg-transparent border border-stone-300 focus:border-stone-900 outline-none px-4 py-3 text-stone-900 placeholder:text-stone-400"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Telefon Numarası *</label>
              <input
                required
                type="tel"
                data-testid="checkout-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="05XX XXX XX XX"
                className="mt-2 w-full bg-transparent border border-stone-300 focus:border-stone-900 outline-none px-4 py-3 text-stone-900 placeholder:text-stone-400"
              />
              <p className="mt-1.5 text-[12px] text-stone-500">Size WhatsApp üzerinden bu numaradan ulaşacağız.</p>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Teslimat Adresi *</label>
              <textarea
                required rows={3}
                data-testid="checkout-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Mahalle, sokak, bina/daire no, ilçe, il, posta kodu"
                className="mt-2 w-full bg-transparent border border-stone-300 focus:border-stone-900 outline-none px-4 py-3 text-stone-900 placeholder:text-stone-400 resize-none"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Sipariş Notu (opsiyonel)</label>
              <textarea
                rows={2}
                data-testid="checkout-note-input"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Özel isteğiniz varsa belirtin..."
                className="mt-2 w-full bg-transparent border border-stone-300 focus:border-stone-900 outline-none px-4 py-3 text-stone-900 placeholder:text-stone-400 resize-none"
              />
            </div>
          </div>

          {error && (
            <div data-testid="checkout-error" className="p-4 border border-red-200 bg-red-50 text-red-800 text-[13.5px] flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>

        {/* RIGHT column: order summary + submit */}
        <aside className="bg-white/60 border border-stone-200 p-6 h-fit lg:sticky lg:top-24">
          <h2 className="font-serif text-2xl text-stone-900">Sipariş Özeti</h2>

          <div className="mt-5 space-y-4 max-h-[320px] overflow-y-auto pr-1">
            {items.map((it) => (
              <div key={it.key} className="flex gap-3">
                <img src={it.image} alt={it.name} className="w-16 h-20 object-cover bg-stone-100" />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[15px] text-stone-900 leading-tight">{it.name}</p>
                  <p className="mt-0.5 text-[11px] text-stone-500">Beden {it.size} · {it.color} · {it.qty} adet</p>
                </div>
                <p className="text-[14px] text-stone-900 whitespace-nowrap">₺{(it.price * it.qty).toLocaleString('tr-TR')}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-stone-200 space-y-1.5 text-[14px]">
            <div className="flex justify-between"><span className="text-stone-600">Ara Toplam</span><span>₺{subtotal.toLocaleString('tr-TR')}</span></div>
            {discount > 0 && (
              <div className="flex justify-between"><span className="text-stone-600">İndirim</span><span className="text-green-800">-₺{discount.toLocaleString('tr-TR')}</span></div>
            )}
            <div className="flex justify-between"><span className="text-stone-600">Kargo</span><span>Ücretsiz</span></div>
          </div>

          <div className="mt-5 pt-5 border-t border-stone-200 flex items-baseline justify-between">
            <span className="text-[11px] tracking-[0.28em] uppercase text-stone-500">Toplam</span>
            <span className="font-serif text-3xl text-stone-900">₺{total.toLocaleString('tr-TR')}</span>
          </div>

          <button
            type="button"
            data-testid="checkout-submit-button"
            onClick={submitAll}
            disabled={!canSubmit}
            className={`mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors ${
              canSubmit
                ? 'bg-stone-900 text-[#f7f2ec] hover:bg-stone-800'
                : 'bg-stone-300 text-stone-600 cursor-not-allowed'
            }`}
          >
            <Send className="h-4 w-4" />
            {submitLabel}
          </button>

          <p className="mt-4 text-[12px] text-stone-500">
            Siparişi Tamamla'ya tıkladığınızda sipariş bilgileriniz hazır bir mesajla{' '}
            <span className="text-stone-800">WhatsApp'a</span> aktarılır. Mesajı göndererek siparişinizi onaylayın.
          </p>

          <a
            data-testid="checkout-whatsapp-contact"
            href={`https://wa.me/${WHATSAPP_INTL}`}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-stone-600 hover:text-stone-900"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp ile iletişim
          </a>
        </aside>
      </div>
    </div>
  );
}
